import React from 'react';

import { IconAlertTriangleFilled } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  ComplianceCourseEnrollmentEntity,
  listEnrollmentsByCourseOptions,
  Plan,
} from '@cfa/pathway-fe-shared/xp';
import { Tooltip } from '@cfa/react-components';

import useAuth from '@/hooks/useAuth';

import { mapCategoryToAliasTranslation } from '@/utils/categoryUtils';
import { itemByLanguage } from '@/utils/language';

import PlanCardCategoryIcon from '@/components/Cards/PlanCard/PlanCardContent/PlanCardCategoryIcon';
import { PathwayCard } from '@/components/pathway-card';

import Constants, { routes } from '@/constants';

import Buttons from './Buttons';
import { enrollmentPassed, useTeamMemberCount } from './utils';

export interface ComplianceCourseWithUserId extends ComplianceCourseEnrollmentEntity {
  userId: string;
}

interface ComplianceCardProps {
  course: Plan;
  onShowEnableCompliancePlan: (id: string, name: string) => void;
}

const ComplianceCard: React.FC<ComplianceCardProps> = ({
  course,
  onShowEnableCompliancePlan,
}) => {
  const { t } = useTranslation();
  const { data: auth } = useAuth();
  const locationsWithAtLeastTrainer =
    auth?.allLocationsWithAtLeastTrainer ?? [];

  const isEnabled = course.status === 'ACTIVE';

  // xp proxy api, need to replace to new xp api when it's ready
  const { data: complianceStatus } = useQuery({
    ...listEnrollmentsByCourseOptions({
      path: { courseId: course.id },
      query: {
        'List of locations to filter': locationsWithAtLeastTrainer.join(),
      },
    }),
    enabled: !!course.id,
  });

  const completedUsers = Object.values(
    complianceStatus?.enrollments ?? {},
  ).filter(enrollmentPassed).length;

  const totalTeamMembers = useTeamMemberCount(
    locationsWithAtLeastTrainer,
    complianceStatus?.enrollments,
  );

  const allUsersCompleted =
    !!totalTeamMembers && completedUsers === totalTeamMembers;
  const percentComplete =
    totalTeamMembers && (completedUsers / totalTeamMembers) * 100;

  const renderInCompleteStatus = () => {
    if (!complianceStatus?.enrollments) {
      return t('Generic.loading');
    }
    if (!totalTeamMembers) {
      return t('Generic.noTeamMembersAssigned');
    }
    return (
      <span className="flex items-center">
        {isEnabled && (
          <Tooltip
            content={t('TrainingPlans.accountability.outOfCompliance')}
            placement="bottom"
          >
            <IconAlertTriangleFilled className="text-feedback-warning-base! mr-1! size-4!" />
          </Tooltip>
        )}
        {`${completedUsers}/${totalTeamMembers} ${t(
          'Generic.teamMembersCompleted',
        )}`}
      </span>
    );
  };

  const showProgress =
    isEnabled &&
    !!totalTeamMembers &&
    !allUsersCompleted &&
    percentComplete !== 100;

  return (
    <PathwayCard.Root
      className="animate-[fadeIn_linear_0.3s]"
      data-testid="ManagePlansPlanCard"
      link={`${routes.plans.managePlan({ planId: course.id })}?compliancePlan=${itemByLanguage(course.name, '')}`}
      noHover={!isEnabled}
    >
      <PathwayCard.Media>
        <PlanCardCategoryIcon
          category={Constants.PLAN_CATEGORIES.COMPLIANCE}
          disabled={!isEnabled}
          disabledCompliance={!isEnabled}
        />
      </PathwayCard.Media>
      <PathwayCard.Body>
        <PathwayCard.Label>
          {t(
            mapCategoryToAliasTranslation(Constants.PLAN_CATEGORIES.COMPLIANCE),
          )}
        </PathwayCard.Label>
        <PathwayCard.Title>{itemByLanguage(course.name, '')}</PathwayCard.Title>
        {allUsersCompleted ? (
          <PathwayCard.Subtitle tone="success" data-testid="PlanTaskCount">
            {t('Generic.completedByAll')}
          </PathwayCard.Subtitle>
        ) : (
          <PathwayCard.Subtitle data-testid="PlanTaskCount">
            {renderInCompleteStatus()}
          </PathwayCard.Subtitle>
        )}
      </PathwayCard.Body>
      {showProgress && (
        <PathwayCard.Progress
          aria-label="Plan progress"
          value={percentComplete}
        />
      )}
      <PathwayCard.Actions>
        <Buttons
          plan={course}
          onShowEnableCompliancePlan={onShowEnableCompliancePlan}
        />
      </PathwayCard.Actions>
    </PathwayCard.Root>
  );
};

interface ComplianceCardWrapperProps {
  course: Plan;
  onShowEnableCompliancePlan: (id: string, name: string) => void;
}

const ComplianceCardWrapper: React.FC<ComplianceCardWrapperProps> = ({
  course,
  ...props
}) => <ComplianceCard {...props} course={course} />;

export default ComplianceCardWrapper;
